/**
 * OverviewStep.jsx — schneller Überblick über die Akte.
 *
 * Wird als erste Step ("Auftrag") angezeigt, sobald eine Akte geöffnet
 * wird. Zeigt auf einen Blick:
 *   • Stammdaten (Auftragsnummer, Status, Kunde, Fahrzeug)
 *   • KPI-Tiles (Fotos, Dokumente, Unterschriften, KI-Analysen)
 *   • Lichtbilder-Strip (erste 8 Thumbnails) mit Klick → Foto-Lightbox
 *   • Hochgeladene Dokumente (Liste mit Download-Buttons)
 *   • Letzte Verlaufseinträge (5 Zeilen)
 *
 * Bewusst KEINE Bearbeitungs-Felder — das Ziel ist ein 5-Sekunden-Blick
 * ob die Akte komplett ist und was der Gutachter geliefert hat.
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ClipboardList, Camera, FileText, PenLine, Sparkles, Hash, Car, User,
  MapPin, Calendar, Clock, Download, Eye, ChevronRight, History as HistoryIcon,
  ShieldCheck, ShieldAlert, AlertTriangle,
} from "lucide-react";
import { StepHeader } from "./case/_shared";
import AuthImg from "./AuthImg";
import PhotoLightbox from "./PhotoLightbox";
import { caseStatusLabel, caseStatusCls } from "@/lib/caseStatus";
import { useAuth } from "@/lib/auth";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const fmtBytes = (b) => {
  if (!b) return "—";
  const u = ["B", "kB", "MB"];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${u[i]}`;
};

// Doc-Type-Mapping → i18n keys. Wir behalten den deutschen Fallback als
// Default, falls die Übersetzung mal nicht vorhanden ist.
const DOC_TYPE_KEYS = {
  dat_calculation: "case_editor.overview.doc_types.dat_calculation",
  signature: "case_editor.overview.doc_types.signature",
  unfallbericht: "case_editor.overview.doc_types.unfallbericht",
  fahrzeugschein: "case_editor.overview.doc_types.fahrzeugschein",
  versicherung: "case_editor.overview.doc_types.versicherung",
  invoice: "case_editor.overview.doc_types.invoice",
  abtretungserklaerung: "case_editor.overview.doc_types.abtretungserklaerung",
  gutachten: "case_editor.overview.doc_types.gutachten",
  other: "case_editor.overview.doc_types.other",
};

export default function OverviewStep({ caseData, onJump }) {
  const { t } = useTranslation();
  const c = caseData || {};
  const { accessToken } = useAuth();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const photos = c.photos || [];
  const documents = c.documents || [];
  const signatures = c.signatures || [];
  const aiAnalyzed = photos.filter((p) => p.ai_damage_description).length;
  const history = (c.history || []).slice(-5).reverse();

  const veh = c.vehicle || {};
  const cust = c.customer || {};
  const owner = c.owner || c.owner_user || {};

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const docHref = (storagePath) =>
    `${backendBase}/api/files/download/${storagePath}?auth=${encodeURIComponent(accessToken || "")}`;

  return (
    <div className="space-y-5" data-testid="step-overview">
      <StepHeader
        icon={ClipboardList}
        title={t("case_editor.overview.title")}
        subtitle={t("case_editor.overview.subtitle")}
      />

      {/* Stammdaten-Block */}
      <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden" data-testid="overview-stammdaten">
        <div className="px-5 py-3.5 border-b border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-zinc-100 text-zinc-600 flex items-center justify-center">
              <Hash className="h-4 w-4" strokeWidth={2} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-400">{t("case_editor.overview.order_number")}</div>
              <div className="font-display font-semibold text-lg text-zinc-900 tabular-nums">
                {c.case_reference || "—"}
              </div>
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${caseStatusCls(c.status)}`}
            data-testid="overview-status"
          >
            {caseStatusLabel(c.status, t)}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 px-5 py-4 text-sm">
          <StammRow icon={User} label={t("case_editor.overview.injured_party")} value={cust.name || `${cust.firstname || ""} ${cust.surname || ""}`.trim() || "—"} />
          <StammRow icon={MapPin} label={t("case_editor.overview.address")} value={cust.address || "—"} />
          <StammRow icon={Calendar} label={t("case_editor.overview.damage_date")} value={cust.accident_date || cust.damage_date || "—"} />
          <StammRow icon={Car} label={t("case_editor.fields.plate")} value={veh.license_plate || "—"} />
          <StammRow icon={Car} label={t("case_editor.overview.vehicle")} value={[veh.make, veh.model].filter(Boolean).join(" ") || "—"} />
          <StammRow icon={Hash} label={t("case_editor.fields.vin").split(" ")[0]} value={veh.vin || "—"} mono />
          <StammRow icon={Calendar} label={t("case_editor.fields.first_registration")} value={veh.first_registration || "—"} />
          <StammRow
            icon={Clock}
            label={t("case_editor.fields.mileage")}
            value={veh.mileage ? `${Number(veh.mileage).toLocaleString("de-DE")} km` : "—"}
          />
          {owner?.full_name && (
            <StammRow icon={User} label={t("case_editor.fields.gutachter")} value={owner.full_name} />
          )}
        </div>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="overview-kpis">
        <KpiTile
          icon={Camera}
          label={t("case_editor.overview.photos")}
          value={photos.length}
          subline={t("case_editor.overview.photo", { count: photos.length })}
          tone="cyan"
          onClick={() => onJump?.("photos")}
          testid="overview-kpi-photos"
        />
        <KpiTile
          icon={FileText}
          label={t("case_editor.overview.documents")}
          value={documents.length}
          subline={t("case_editor.overview.doc", { count: documents.length })}
          tone="emerald"
          onClick={() => onJump?.("preview")}
          testid="overview-kpi-documents"
        />
        <KpiTile
          icon={PenLine}
          label={t("case_editor.overview.signatures")}
          value={signatures.length}
          subline={signatures.length > 0 ? t("case_editor.overview.sig_done") : t("case_editor.overview.sig_pending")}
          tone={signatures.length > 0 ? "emerald" : "amber"}
          onClick={() => onJump?.("signature")}
          testid="overview-kpi-signatures"
        />
        <KpiTile
          icon={Sparkles}
          label={t("case_editor.overview.ai_analyses")}
          value={aiAnalyzed}
          subline={photos.length > 0 ? t("case_editor.overview.ai_progress", { analyzed: aiAnalyzed, total: photos.length }) : "—"}
          tone="violet"
          onClick={() => onJump?.("photos")}
          testid="overview-kpi-ai"
        />
      </div>

      {/* Unfallbericht-Card — entweder Upload ODER Ersatz-Angaben. */}
      <UnfallberichtCard
        documents={documents}
        insurance={c.insurance || {}}
        customer={cust}
        onJump={onJump}
        t={t}
      />

      {/* Photo-Strip */}
      {photos.length > 0 && (
        <Card title={t("case_editor.overview.photos_card_title", { count: photos.length })} onMore={() => onJump?.("photos")} testid="overview-photos-card" tFn={t}>
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {photos.slice(0, 16).map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setLightboxIndex(i)}
                className="relative aspect-square rounded-lg overflow-hidden border border-zinc-200 hover:ring-2 hover:ring-cyan-400 transition group"
                data-testid={`overview-photo-${i}`}
                title={p.filename || `Foto #${i + 1}`}
              >
                <AuthImg
                  path={p.storage_path}
                  token={accessToken}
                  alt={p.filename || ""}
                  className="w-full h-full object-cover"
                />
                {p.ai_damage_description && (
                  <div className="absolute top-1 right-1 h-4 w-4 rounded-full bg-[#12A9E8] text-white flex items-center justify-center">
                    <Sparkles className="h-2.5 w-2.5" strokeWidth={2.5} />
                  </div>
                )}
              </button>
            ))}
            {photos.length > 16 && (
              <button
                type="button"
                onClick={() => onJump?.("photos")}
                className="aspect-square rounded-lg border-2 border-dashed border-zinc-300 hover:border-cyan-400 hover:bg-cyan-50/50 flex flex-col items-center justify-center text-xs font-semibold text-zinc-600 hover:text-cyan-700 transition"
                data-testid="overview-photos-more"
              >
                +{photos.length - 16}
                <span className="text-[10px] font-normal mt-0.5">{t("case_editor.overview.more")}</span>
              </button>
            )}
          </div>
        </Card>
      )}

      {/* Documents */}
      {documents.length > 0 && (
        <Card title={t("case_editor.overview.docs_card_title", { count: documents.length })} testid="overview-docs-card" tFn={t}>
          <div className="divide-y divide-zinc-100">
            {documents.map((d, i) => (
              <div key={d.id || i} className="py-2.5 flex items-center gap-3" data-testid={`overview-doc-${i}`}>
                <div className="h-9 w-9 rounded-lg bg-zinc-100 text-zinc-600 flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate">
                    {d.label || d.filename || t("case_editor.overview.doc_types.other")}
                  </div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-2">
                    <span>{DOC_TYPE_KEYS[d.type] ? t(DOC_TYPE_KEYS[d.type]) : (d.type || t("case_editor.overview.file"))}</span>
                    <span>·</span>
                    <span>{fmtBytes(d.size)}</span>
                    <span>·</span>
                    <span>{fmtDate(d.uploaded_at)}</span>
                  </div>
                </div>
                <a
                  href={docHref(d.storage_path)}
                  download
                  className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-zinc-200 text-xs font-semibold text-zinc-700 hover:border-cyan-300 hover:text-cyan-700 transition"
                  data-testid={`overview-doc-${i}-download`}
                  title={t("case_editor.download_zip")}
                >
                  <Download className="h-3 w-3" />
                </a>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card title={t("case_editor.overview.history")} testid="overview-history-card" tFn={t}>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="flex items-start gap-3 text-xs" data-testid={`overview-history-${i}`}>
                <div className="h-7 w-7 shrink-0 rounded-full bg-zinc-100 text-zinc-600 flex items-center justify-center">
                  <HistoryIcon className="h-3 w-3" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-900">{h.action || h.text || "—"}</div>
                  <div className="text-[10px] text-zinc-500">{fmtDate(h.ts || h.at)}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Empty State */}
      {photos.length === 0 && documents.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-zinc-200 bg-zinc-50/40 p-10 text-center" data-testid="overview-empty">
          <div className="h-16 w-16 mx-auto rounded-full bg-zinc-100 text-zinc-400 flex items-center justify-center mb-3">
            <ClipboardList className="h-7 w-7" strokeWidth={1.5} />
          </div>
          <div className="text-sm font-semibold text-zinc-700">{t("case_editor.overview.empty_title")}</div>
          <div className="text-xs text-zinc-500 mt-1">
            {t("case_editor.overview.empty_subtitle")}
          </div>
        </div>
      )}

      {/* Lightbox aus Overview heraus */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onDelete={async () => {
            // Read-only Overview — Löschen nur über den Foto-Step erlaubt.
            // (Wir geben dem User einen sanften Hinweis statt Operation.)
            onJump?.("photos");
          }}
        />
      )}
    </div>
  );
}


function StammRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-2.5 min-w-0">
      {Icon && <Icon className="h-3.5 w-3.5 text-zinc-400 mt-0.5 shrink-0" strokeWidth={2} />}
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
        <div className={`${mono ? "font-mono text-xs" : "text-sm"} text-zinc-900 font-medium truncate`} title={value}>
          {value || "—"}
        </div>
      </div>
    </div>
  );
}


function KpiTile({ icon: Icon, label, value, subline, tone, onClick, testid }) {
  const iconTone = {
    cyan: "bg-cyan-50 text-cyan-600",
    emerald: "bg-sky-50 text-[#12A9E8]",
    violet: "bg-violet-50 text-violet-600",
    amber: "bg-amber-50 text-amber-600",
  }[tone] || "bg-zinc-100 text-zinc-500";

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-xl border border-zinc-200 bg-white p-4 transition-colors hover:border-zinc-300 hover:shadow-sm"
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${iconTone}`}>
          <Icon className="h-4 w-4" strokeWidth={2} />
        </div>
        <ChevronRight className="h-4 w-4 text-zinc-300" />
      </div>
      <div className="text-2xl font-display font-bold tabular-nums leading-none mt-3 text-zinc-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-semibold mt-1.5 text-zinc-500">{label}</div>
      <div className="text-[11px] text-zinc-400 mt-0.5">{subline}</div>
    </button>
  );
}


function Card({ title, children, onMore, testid, tFn }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-zinc-100 bg-zinc-50/40 flex items-center justify-between">
        <div className="text-sm font-display font-semibold text-zinc-900">{title}</div>
        {onMore && (
          <button
            type="button"
            onClick={onMore}
            className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-800"
          >
            {tFn ? tFn("case_editor.overview.view_all") : "Alle ansehen"}
            <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}


/**
 * UnfallberichtCard — zeigt entweder den hochgeladenen Bericht
 * (Dokument-Typ ``unfallbericht`` oder Dateiname enthält "unfall") ODER
 * die *Ersatz-Angaben* (Gegner-Kennzeichen, Schadenort, Schadentag) mit
 * deutlichem Hinweis für den Admin.
 *
 * Das ist ein wichtiges UX-Pattern: der Gutachter muss entweder den
 * Bericht hochladen ODER die Pflichtfelder ausfüllen — der Admin sieht
 * sofort welcher Pfad gewählt wurde.
 */
function UnfallberichtCard({ documents, insurance, customer, onJump, t }) {
  const upload = (documents || []).find((d) => {
    const tp = (d.type || "").toLowerCase();
    const f = (d.filename || "").toLowerCase();
    return tp === "unfallbericht" || f.includes("unfallber") || f.includes("accident");
  });

  if (upload) {
    return (
      <div
        className="rounded-xl border border-zinc-200 bg-white p-4 sm:p-5 flex items-center gap-4"
        data-testid="overview-unfallbericht-uploaded"
      >
        <div className="h-10 w-10 rounded-lg bg-sky-50 text-[#12A9E8] flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-400">{t("case_editor.overview.unfallbericht")}</div>
          <div className="font-display font-semibold text-base text-zinc-900 truncate">
            {upload.label || upload.filename || t("case_editor.overview.unfallbericht_attached")}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {upload.size ? `${Math.round(upload.size / 1024)} kB` : ""}
            {upload.uploaded_at ? ` · ${t("case_editor.overview.uploaded_on", { date: new Date(upload.uploaded_at).toLocaleDateString() })}` : ""}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onJump?.("preview")}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-200 text-xs font-semibold text-zinc-700 hover:border-[#12A9E8]/50 hover:text-[#12A9E8] transition-colors shrink-0"
          data-testid="overview-unfallbericht-open"
        >
          <Eye className="h-3.5 w-3.5" />
          {t("case_editor.overview.view")}
        </button>
      </div>
    );
  }

  // Kein Upload → Ersatz-Angaben (vom Gutachter eingetragen)
  const plate = customer?.opponent_plate || "";
  const where = insurance?.damage_location || "";
  const when = insurance?.claim_date || "";
  const allFilled = plate && where && when;

  return (
    <div
      className="rounded-xl border border-zinc-200 bg-white p-4 sm:p-5"
      data-testid="overview-unfallbericht-fallback"
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
          allFilled ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
        }`}>
          {allFilled ? <ShieldCheck className="h-5 w-5" strokeWidth={2} /> : <ShieldAlert className="h-5 w-5" strokeWidth={2} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-400">
            {t("case_editor.overview.unfallbericht_fallback_title")}
          </div>
          <div className="font-display font-semibold text-base text-zinc-900">
            {allFilled
              ? t("case_editor.overview.unfallbericht_ok")
              : t("case_editor.overview.unfallbericht_missing")}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {allFilled
              ? t("case_editor.overview.unfallbericht_ok_sub")
              : t("case_editor.overview.unfallbericht_missing_sub")}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onJump?.("insurance")}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-200 text-xs font-semibold text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50 transition-colors shrink-0"
          data-testid="overview-unfallbericht-edit"
        >
          {allFilled ? t("case_editor.overview.edit") : t("case_editor.overview.complete")}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-zinc-100">
        <FallbackField
          icon={Car} label={t("case_editor.fields.opponent_plate")} value={plate}
          missing={!plate} testid="overview-unfall-plate" t={t}
        />
        <FallbackField
          icon={MapPin} label={t("case_editor.fields.damage_location")} value={where}
          missing={!where} testid="overview-unfall-location" t={t}
        />
        <FallbackField
          icon={Calendar} label={t("case_editor.fields.claim_date")}
          value={when ? new Date(when).toLocaleDateString() : ""}
          missing={!when} testid="overview-unfall-date" t={t}
        />
      </div>
    </div>
  );
}


function FallbackField({ icon: Icon, label, value, missing, testid, t }) {
  return (
    <div className="flex items-start gap-2.5 min-w-0" data-testid={testid}>
      <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${missing ? "text-amber-500" : "text-zinc-400"}`} strokeWidth={2} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
        {missing ? (
          <div className="inline-flex items-center gap-1 text-xs text-amber-700 font-semibold mt-0.5">
            <AlertTriangle className="h-3 w-3" /> {t ? t("case_editor.overview.missing") : "fehlt"}
          </div>
        ) : (
          <div className="text-sm text-zinc-900 font-medium truncate" title={value}>{value}</div>
        )}
      </div>
    </div>
  );
}
